import React, {useEffect, useRef, useState} from 'react';
import {useGameConfig} from '../../context/GameConfig';
import './CameraStreams.css';

const STAGGER_DELAY_MS = 500;
const IFRAME_BASE_WIDTH = 1280;
const IFRAME_BASE_HEIGHT = 720;

const CameraFrame = ({ src, title }) => {
    const containerRef = useRef(null);
    const [scale, setScale] = useState(1);

    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver(([entry]) => {
            setScale(entry.contentRect.width / IFRAME_BASE_WIDTH);
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={containerRef} className="camera-frame">
            <iframe
                src={src}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: `${IFRAME_BASE_WIDTH}px`,
                    height: `${IFRAME_BASE_HEIGHT}px`,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    border: 'none',
                }}
                loading="lazy"
                allowFullScreen
                allow="autoplay"
                title={title}
            />
        </div>
    );
};

const Col1Icon = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
        <rect x="3" y="2" width="14" height="16" rx="2"/>
    </svg>
);

const Col2Icon = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
        <rect x="2" y="2" width="6" height="16" rx="1.5"/>
        <rect x="12" y="2" width="6" height="16" rx="1.5"/>
    </svg>
);

const Col3Icon = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
        <rect x="1" y="2" width="5" height="16" rx="1.5"/>
        <rect x="7.5" y="2" width="5" height="16" rx="1.5"/>
        <rect x="14" y="2" width="5" height="16" rx="1.5"/>
    </svg>
);

const SpotlightIcon = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
        <rect x="1" y="1" width="18" height="11" rx="1.5"/>
        <rect x="1" y="14" width="5" height="5" rx="1"/>
        <rect x="7.5" y="14" width="5" height="5" rx="1"/>
        <rect x="14" y="14" width="5" height="5" rx="1"/>
    </svg>
);

const CameraStreams = ({gameId, roomConfig}) => {
    const {fetchStreams, verifyAndFetchStreamUrl} = useGameConfig([]);
    const [cameraUrls, setCameraUrls] = useState([]);
    const [loadedCount, setLoadedCount] = useState(0);
    const [layout, setLayout] = useState(1);
    const [featuredIndex, setFeaturedIndex] = useState(0);

    const sortCameraUrls = (urls) => {
        return [...urls].sort((a, b) => a.order - b.order);
    };

    useEffect(() => {
        console.log(`Connecting to streams for game: ${gameId}`);
        const streams = fetchStreams(gameId);
        Promise.all(streams.map((streamConfig) => verifyAndFetchStreamUrl(streamConfig)))
            .then((results) => {
                setCameraUrls(sortCameraUrls(results.filter(Boolean)));
                setLoadedCount(0);
            });
    }, [roomConfig, gameId]);

    // Stagger iframe src assignment to avoid hammering hardware all at once
    useEffect(() => {
        if (loadedCount >= cameraUrls.length) return;
        const timer = setTimeout(() => setLoadedCount((c) => c + 1), STAGGER_DELAY_MS);
        return () => clearTimeout(timer);
    }, [loadedCount, cameraUrls.length]);

    const toolbar = (
        <div className="layout-toolbar">
            <button className={`layout-btn${layout === 1 ? ' active' : ''}`} onClick={() => setLayout(1)} title="1 column">
                <Col1Icon />
            </button>
            <button className={`layout-btn${layout === 2 ? ' active' : ''}`} onClick={() => setLayout(2)} title="2 columns">
                <Col2Icon />
            </button>
            <button className={`layout-btn${layout === 3 ? ' active' : ''}`} onClick={() => setLayout(3)} title="3 columns">
                <Col3Icon />
            </button>
            <button className={`layout-btn${layout === 'spotlight' ? ' active' : ''}`} onClick={() => setLayout('spotlight')} title="Spotlight">
                <SpotlightIcon />
            </button>
        </div>
    );

    if (layout === 'spotlight') {
        const featured = cameraUrls[featuredIndex];
        return (
            <div>
                {toolbar}
                <div className="spotlight-container">
                    <div className="spotlight-main">
                        {featured && (
                            <iframe
                                src={featured.src}
                                allowFullScreen
                                allow="autoplay"
                                title={featured.name}
                            />
                        )}
                    </div>
                    <div className="spotlight-strip">
                        {cameraUrls.map((url, index) => (
                            <div key={url.name} className={`spotlight-thumb${index === featuredIndex ? ' spotlight-thumb--active' : ''}`} onClick={() => setFeaturedIndex(index)} title={url.name}>
                                <iframe
                                    src={url.src}
                                    allow="autoplay"
                                    scrolling="no"
                                    title={url.name}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            {toolbar}
            <div id="camera-frames" className={`layout-${layout}`}>
                {cameraUrls.map((url, index) => (
                    <CameraFrame
                        key={url.name}
                        src={index < loadedCount ? url.src : undefined}
                        title={url.name}
                    />
                ))}
            </div>
        </div>
    );
};

export default CameraStreams;